<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'

const props = defineProps<{
  date?: string
  accentColor?: string
  label?: string
  error?: boolean
  errorMessages?: string[]
  rules?: (() => string | boolean)[]
}>()

const emit = defineEmits<{
  (e: 'change', value: string): void
}>()

const date = computed({
  get: () => props.date || '',
  set: (value) => emit('change', value || ''),
})

const error = computed(
  () => props.error || false || errorMessages.value.length > 0,
)

const errorMessages = ref<string[]>(props.errorMessages || [])

onMounted(() => {
  if (props.rules) {
    props.rules.forEach((rule) => {
      //errorMessages.value.push(rule());
      const result = rule()
      if (typeof result === 'string') {
        errorMessages.value.push(result)
      }
    })
  }
})

watch(date, (newDate) => {
  emit('change', newDate || '')
})

const handleInputClick = (evt: MouseEvent) => {
  try {
    const target = evt.currentTarget as HTMLInputElement
    target.showPicker()
  } catch (error) {
    console.error(error)
  }
}

const handleWrapperClick = (evt: MouseEvent) => {
  if ((evt.currentTarget as HTMLElement).children.length > 1) {
    ;(
      (evt.currentTarget as HTMLElement).children[1] as HTMLInputElement
    ).focus()
  }
}

function handleInputChange(evt: Event) {
  const target = evt.currentTarget as HTMLInputElement
  date.value = target.value
}
</script>

<template>
  <div
    class="wheels-date-picker wheels-form-field"
    :class="{ 'wheels-form-error': error || errorMessages.length > 0 }"
  >
    <label class="wheels-form-label" v-if="label">{{ label }}</label>
    <span class="wheels-form-field__input-wrapper" @click="handleWrapperClick">
      <input
        @click="handleInputClick"
        class="wheels-form-field__input"
        type="date"
        @change="handleInputChange"
        :value="date"
      />
    </span>
    <div v-if="errorMessages.length > 0">
      <span
        v-for="(errorMessage, idx) in errorMessages"
        :key="`error-${idx}`"
        class="wheels-form-error-message"
        >{{ errorMessage }}</span
      >
    </div>
  </div>
</template>

<style scoped lang="scss">
.wheels-date-picker {
  border: 2px solid var(--cu-gray-100, #f3f4f6);
  border-radius: 8px;
  padding: 0;
  min-height: 48px;
}
.wheels-form-field__input-wrapper {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-1);

  min-height: 44px;
  border-radius: 8px;
  padding: 0 12px;
  background: white;
  width: 90%;
  transition: border-color 0.15s ease;

  &:hover {
    border-color: var(--cu-blue-600);
  }

  &:focus-within {
    border-color: var(--cu-blue-600);
  }
}

.wheels-form-field__input {
  font-family: var(--font-family-sans);
  margin: 0;
  font-size: 16px;

  border: none;
  outline: none;
  background: transparent;
  flex: 1;
}

.wheels-form-field__input-icon {
  width: 1rem;
}

.wheels-form-field__input-wrapper:after {
  content: '';
  display: block;
  width: 1.3rem;
  height: auto;
  background-color: rgba(255, 255, 255, 1);
  position: absolute;
  top: 0;
  right: -3px;
  bottom: 0;
  z-index: 2;
}

input[type='date']::-webkit-inner-spin-button,
input[type='date']::-webkit-calendar-picker-indicator {
  display: none;
  -webkit-appearance: none;
}
</style>
